import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../api";
import { Icon } from "../lib/Icon";
import { ErrorNote, Loading, Page } from "./common";

const CARD: React.CSSProperties = { background: "#fff", borderRadius: 3, boxShadow: "inset 0 0 0 1px rgba(0,0,0,.07)", padding: "15px 18px" };
const FIELD: React.CSSProperties = { fontSize: 13, padding: "8px 10px", border: "1px solid #e2e0d8", borderRadius: 7 };

export function TrustedVendors() {
  const qc = useQueryClient();
  const { data, isLoading, error } = useQuery({ queryKey: ["trusted-vendors"], queryFn: api.trustedVendors });
  const [entry, setEntry] = useState("");
  const [addErr, setAddErr] = useState<string | null>(null);

  const invalidate = () => qc.invalidateQueries({ queryKey: ["trusted-vendors"] });
  const add = useMutation({
    mutationFn: (v: string) => api.addTrustedVendor(v),
    onMutate: () => setAddErr(null),
    onSuccess: (r) => {
      if (r.error) { setAddErr(r.error); return; }
      setEntry("");
      invalidate();
    },
    onError: (e) => setAddErr(e instanceof Error ? e.message : "Couldn't add"),
  });
  const remove = useMutation({ mutationFn: (v: string) => api.removeTrustedVendor(v), onSuccess: invalidate });

  if (isLoading) return <Loading />;
  if (error || !data) return <ErrorNote error={error} />;
  const canManage = data.canManage;
  const submit = () => { const v = entry.trim(); if (v && !add.isPending) add.mutate(v); };

  return (
    <Page>
      <div>
        <h1 style={{ margin: 0, fontSize: 24, fontWeight: 600, letterSpacing: "-0.6px" }}>Trusted senders</h1>
        <div style={{ fontSize: 14, color: "#8a8e88", marginTop: 3, maxWidth: 680 }}>
          Vendors and partners whose <strong>identity</strong> you trust — mail from them won’t be flagged as an “unknown sender”.
          Add a whole domain (<code>skyslope.com</code>) or a specific address (<code>alerts@datadog.com</code>). Every domain in your
          Cloudflare account and each client’s own domain are already trusted automatically.
        </div>
      </div>

      <div style={{ ...CARD, background: "#fbf6e9", boxShadow: "inset 0 0 0 1px #efe2c0", fontSize: 12.5, color: "#7a5f14", display: "flex", gap: 8, alignItems: "flex-start" }}>
        <Icon name="shield-alert" size={16} color="#9a7b1f" />
        <span>Trusting a sender only skips the unknown-sender flag. Their messages are <strong>still fully scanned</strong> for phishing links, dangerous attachments, and suspicious requests — because a trusted account can be taken over. You can’t trust a shared email provider (like gmail.com) as a whole domain; add the exact address instead.</span>
      </div>

      {canManage && (
        <div style={CARD}>
          <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 10 }}>Add a trusted sender</div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
            <input
              value={entry}
              onChange={(e) => setEntry(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") submit(); }}
              placeholder="skyslope.com  or  alerts@datadog.com"
              style={{ ...FIELD, flex: "1 1 280px" }}
            />
            <button
              onClick={submit}
              disabled={!entry.trim() || add.isPending}
              style={{ fontSize: 12.5, fontWeight: 600, color: "#fff", background: entry.trim() && !add.isPending ? "#006747" : "#b7c3bd", border: 0, borderRadius: 8, padding: "8px 16px", cursor: entry.trim() && !add.isPending ? "pointer" : "default" }}
            >
              {add.isPending ? "Adding…" : "Add"}
            </button>
          </div>
          {addErr && <div style={{ fontSize: 11.5, color: "#b3261e", marginTop: 7 }}>{addErr}</div>}
        </div>
      )}

      <div style={CARD}>
        <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 8 }}>Trusted list <span style={{ fontSize: 12, color: "#8a8e88", fontWeight: 400 }}>({data.vendors.length})</span></div>
        {data.vendors.length === 0 ? (
          <div style={{ fontSize: 13, color: "#8a8e88" }}>No trusted vendors added yet.</div>
        ) : (
          data.vendors.map((v, i) => {
            const removing = remove.isPending && remove.variables === v;
            return (
              <div key={v} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, padding: "8px 0", borderTop: i ? "1px solid #f4f3ee" : "none", fontSize: 13 }}>
                <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                  <Icon name={v.includes("@") && !v.startsWith("@") ? "user-round" : "building-2"} size={14} color="#8a8e88" />
                  <span style={{ fontFamily: "'Space Mono',monospace", fontSize: 12.5 }}>{v.startsWith("@") ? v.slice(1) : v}</span>
                  <span style={{ fontSize: 11, color: "#a8aca6" }}>{v.includes("@") && !v.startsWith("@") ? "address" : "domain"}</span>
                </span>
                {canManage && (
                  <button onClick={() => !remove.isPending && remove.mutate(v)} disabled={remove.isPending} title="Remove" aria-label={`Remove ${v}`} style={{ display: "inline-flex", alignItems: "center", background: "none", border: 0, cursor: remove.isPending ? "default" : "pointer", opacity: removing ? 0.4 : 1 }}>
                    <Icon name="x" size={14} color="#b3261e" />
                  </button>
                )}
              </div>
            );
          })
        )}
      </div>
    </Page>
  );
}
